import type { Message } from "../state/chatStore";
import type { ReactNode } from "react";

type TtsState = "idle" | "playing" | "failed";

type Props = {
  message: Message;
  ttsState?: TtsState;
  onReplayTts?: () => void;
  onRetry?: () => void;
  onEditText?: () => void;
  children?: ReactNode;
  // streaming STT (spec: docs/superpowers/specs/2026-07-07-m3-continuous-conversation-requirements.md §3.12 / R3.12.2):
  // streaming Scribe の partial_transcript を薄色 (opacity-60) で表示
  partial?: boolean;
};

export function MessageBubble({
  message,
  ttsState = "idle",
  onReplayTts,
  onRetry,
  onEditText,
  children,
  partial,
}: Props) {
  const isUser = message.kind === "user-transcript";
  const isConfirm = message.kind === "ai-confirm";

  const rowClass =
    (isUser ? "flex flex-col items-end gap-1" : "flex flex-col items-start gap-1") +
    (partial ? " opacity-60" : "");

  const bubbleClass = isUser
    ? "bg-indigo-600 text-white rounded-2xl rounded-tr-sm px-3 py-2 max-w-[85%] text-sm leading-relaxed"
    : isConfirm
      ? "bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-2xl rounded-tl-sm px-3 py-1.5 text-xs"
      : "bg-white text-slate-900 border border-slate-200 rounded-2xl rounded-tl-sm px-3 py-2 max-w-[85%] text-sm leading-relaxed";

  return (
    <div className={rowClass} data-testid={`message-${message.id}`}>
      <div className={bubbleClass}>
        {!isUser && (
          // 🔊 は実寸 44x44 (Apple HIG のタップ領域) にし、負 margin で margin box を
          // 元の 20x24 に戻すのでレイアウトは 1px も増えない (AI バブルは 1 ターンに
          // 2 個積まれるため実寸で太らせない)。-mx-3 はこの行の gap-3 とバブルの px-3。
          // **relative は必須** — 負 margin で下へ出した 10px のうち mb-1 の 4px を超える
          // 6px は、同じバブルの本文 (行ボックス) と重なる。非 positioned な block-level の
          // button (CSS 2.1 Appendix E step 4) は後から描かれる inline content (step 6) に
          // ヒットテストを奪われるため、実測で押せる高さが 44 ではなく 37px になっていた
          // (box は 44 のまま = getBoundingClientRect では検出できない)。positioned に
          // すると step 8 で最後に描かれ、レイアウトを 1px も変えずに 44px 全域が押せる。
          <div className="flex items-center gap-3 mb-1 text-xs font-semibold">
            <span className="bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">AI</span>
            {onReplayTts && (
              <button
                type="button"
                onClick={onReplayTts}
                aria-label="読み上げ"
                className={
                  "relative shrink-0 inline-flex items-center justify-center w-11 h-11 -my-2.5 -mx-3 text-base " +
                  (ttsState === "playing"
                    ? "text-indigo-600 animate-pulse"
                    : ttsState === "failed"
                      ? "text-amber-600"
                      : "text-slate-400 hover:text-indigo-600")
                }
              >
                🔊
              </button>
            )}
          </div>
        )}
        {message.text}
        {children}
      </div>
      {isUser && (onRetry || onEditText) && (
        // ↻ / ✏️ は min-h-[44px] (Apple HIG のタップ領域) を実寸で持ち、はみ出す分だけを
        // 負 margin で下側の死に領域に畳む。**-mb-2 は thread (ChatScreen) の gap-2 と
        // 厳密に一致していること** — 余白より大きくすると当たり判定が次の AI バブルに乗る。
        // **上へは畳まない (-mt-* を足さない)**: ↻ は確認なしで回答とそれ以降の会話を
        // すべて消すので、回答バブルとの間に rowClass の gap-1 (4px) を死に領域として
        // 残す。上へ 4px 畳むと当たり判定が回答バブルの下端に密着し、自分の回答の文字を
        // 触ろうとした指が ↻ を踏む (実測: 畳むと死に領域 0px、外すと 4px)。
        // 横は px-2 で 16px 広げてある (実測 51.2 → 77.2px)。横には負 margin が無いので
        // 2 つの当たり判定の間隔はこの行の gap がそのまま出る。8px (Material の 8dp) では
        // なく gap-3 (12px) にしてあるのは、↻ が破壊的で ✏️ がそうでないため — 取り違えの
        // コストが左右で違う組み合わせは最小値ではなく余裕を持たせる (実測 12px)。
        <div className="flex gap-3 px-2 text-xs text-slate-500">
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              aria-label="やり直す"
              className="inline-flex items-center min-h-[44px] px-2 hover:text-indigo-600 -mb-2"
            >
              ↻ やり直す
            </button>
          )}
          {onEditText && (
            <button
              type="button"
              onClick={onEditText}
              aria-label="文字を直す"
              className="inline-flex items-center min-h-[44px] px-2 hover:text-indigo-600 -mb-2"
            >
              ✏️ 文字を直す
            </button>
          )}
        </div>
      )}
    </div>
  );
}
